import { useCallback, useId, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import styles from './Accordion.module.scss';

// ============================================
// TYPES
// ============================================

export type AccordionChevronPosition = 'left' | 'right';
export type AccordionVariant = 'plain' | 'filled';

/** Props for the Accordion component. */
export interface AccordionProps {
  /** Accordion title text */
  title: string;
  /** Optional subtitle/explainer text below the title */
  subtitle?: string;
  /** Optional icon element rendered before the title */
  icon?: ReactNode;
  /** Position of the expand/collapse chevron */
  chevronPosition?: AccordionChevronPosition;
  /** Visual variant — plain (transparent) or filled (tinted background) */
  variant?: AccordionVariant;
  /** White body background — extends full width inside filled variant with bottom border-radius */
  whiteBody?: boolean;
  /** Controlled expanded state. When provided, the component is controlled. */
  expanded?: boolean;
  /** Initial expanded state for uncontrolled usage */
  defaultExpanded?: boolean;
  /** Callback fired when the expanded state changes */
  onExpandedChange?: (expanded: boolean) => void;
  /** Optional action element rendered in the header (e.g., edit icon). Rendered outside the trigger button. */
  action?: ReactNode;
  /** Body content — any ReactNode */
  children: ReactNode;
  /** Additional CSS class on the root element */
  className?: string;
}

// ============================================
// CHEVRON ICON
// ============================================

function ChevronIcon() {
  return (
    <svg
      className={styles.chevronIcon}
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ============================================
// COMPONENT
// ============================================

/**
 * Accordion — A disclosure component that shows/hides content sections.
 *
 * Supports controlled (`expanded` + `onExpandedChange`) and uncontrolled
 * (`defaultExpanded`) modes. Body content is fully flexible via `children`.
 *
 * @example
 * // Uncontrolled, right chevron (default)
 * <Accordion title="Section Title">
 *   <p>Any content here</p>
 * </Accordion>
 *
 * @example
 * // Controlled, filled variant, left chevron, with subtitle
 * <Accordion
 *   title="Settings"
 *   subtitle="Configure your preferences"
 *   variant="filled"
 *   chevronPosition="left"
 *   expanded={isOpen}
 *   onExpandedChange={setIsOpen}
 * >
 *   <SettingsForm />
 * </Accordion>
 */
function Accordion({
  title,
  subtitle,
  icon,
  chevronPosition = 'right',
  variant = 'plain',
  whiteBody = false,
  expanded: controlledExpanded,
  defaultExpanded = false,
  onExpandedChange,
  action,
  children,
  className,
}: AccordionProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  const isControlled = controlledExpanded !== undefined;
  const isExpanded = isControlled ? controlledExpanded : internalExpanded;

  const handleToggle = useCallback(() => {
    const next = !isExpanded;
    if (!isControlled) {
      setInternalExpanded(next);
    }
    onExpandedChange?.(next);
  }, [isExpanded, isControlled, onExpandedChange]);

  const uniqueId = useId();
  const triggerId = `accordion-trigger-${uniqueId}`;
  const panelId = `accordion-panel-${uniqueId}`;

  const rootClassName = useMemo(() => {
    const classes = [styles.root];
    if (variant === 'filled') classes.push(styles.filled);
    if (whiteBody) classes.push(styles.whiteBody);
    if (isExpanded) classes.push(styles.expanded);
    if (chevronPosition === 'left') classes.push(styles.chevronLeft);
    if (className) classes.push(className);
    return classes.join(' ');
  }, [variant, whiteBody, isExpanded, chevronPosition, className]);

  const chevron = (
    <span className={styles.chevron}>
      <ChevronIcon />
    </span>
  );

  return (
    <div className={rootClassName}>
      <div className={styles.header}>
        <button
          type="button"
          id={triggerId}
          className={styles.trigger}
          onClick={handleToggle}
          aria-expanded={isExpanded}
          aria-controls={panelId}
        >
          {chevronPosition === 'left' && chevron}
          {icon && <span className={styles.icon}>{icon}</span>}
          <span className={styles.titleGroup}>
            <span className={styles.title}>{title}</span>
            {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
          </span>
          {chevronPosition === 'right' && chevron}
        </button>
        {action && (
          <span
            className={styles.action}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {action}
          </span>
        )}
      </div>

      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        className={styles.panel}
      >
        <div className={styles.panelContent}>{children}</div>
      </div>
    </div>
  );
}

export default Accordion;
